import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import './DeliveryBoyApp.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://vakra.co.in';
const DELIVERY_API = `${BASE_URL}/api/delivery-dispatch`;

const TRIP_STATUS_LABELS = {
  assigned: 'Assigned',
  picked_up: 'Picked Up',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const ORDER_STATUS_LABELS = {
  assigned: 'Assigned',
  picked_up: 'Picked Up',
  out_for_delivery: 'Out for Delivery',
  reached: 'Reached',
  delivered: 'Delivered',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const DeliveryBoyApp = () => {
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const vendorFromUrl = queryParams.get('vendor') || queryParams.get('vendorId') || '';
  const vendorTypeFromUrl =
    queryParams.get('vendorType') ||
    queryParams.get('vendor_type') ||
    'fastfood';

  const [vendorId, setVendorId] = useState(vendorFromUrl);
  const [vendorType, setVendorType] = useState(
    String(vendorTypeFromUrl || 'fastfood').toLowerCase()
  );

  const [loginForm, setLoginForm] = useState({
    mobile: '',
    pin: '',
  });

  const [rider, setRider] = useState(() => {
    try {
      const saved = localStorage.getItem('delivery_boy_rider');
      if (!saved) return null;

      const parsed = JSON.parse(saved);
      const savedVendorId = String(
        parsed?.vendorId || parsed?.vendor_id || ''
      );
      const savedVendorType = String(
        parsed?.vendorType || parsed?.vendor_type || 'fastfood'
      ).toLowerCase();

      const currentVendorId = String(vendorFromUrl || '');
      const currentVendorType = String(
        vendorTypeFromUrl || 'fastfood'
      ).toLowerCase();

      const vendorChanged =
        (currentVendorId && savedVendorId && currentVendorId !== savedVendorId) ||
        (currentVendorType && savedVendorType !== currentVendorType);

      if (vendorChanged) {
        localStorage.removeItem('delivery_boy_rider');
        return null;
      }

      return parsed;
    } catch {
      localStorage.removeItem('delivery_boy_rider');
      return null;
    }
  });

  const [activeTrip, setActiveTrip] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [mapOrder, setMapOrder] = useState(null);
  const [tokenByOrder, setTokenByOrder] = useState({});
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);

  const [loading, setLoading] = useState(false);
  const [tripLoading, setTripLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const watchIdRef = useRef(null);
  const lastLocationSentAtRef = useRef(0);
  const riderRef = useRef(rider);
  const activeTripRef = useRef(activeTrip);

  useEffect(() => {
    riderRef.current = rider;
  }, [rider]);

  useEffect(() => {
    activeTripRef.current = activeTrip;
  }, [activeTrip]);

  const showMessage = useCallback((text, type = 'success', duration = 3200) => {
    setMessage(text);
    setMessageType(type);
    window.clearTimeout(showMessage._timer);
    showMessage._timer = window.setTimeout(() => setMessage(''), duration);
  }, []);

  const money = (value) => {
    const num = Number(value || 0);
    return `₹${Math.round(Number.isFinite(num) ? num : 0).toLocaleString('en-IN')}`;
  };

  const dateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const normalizeOrder = (order) => ({
    ...order,
    id: order?.id || order?.trip_order_id || order?.tripOrderId,
    tripOrderId: order?.trip_order_id || order?.tripOrderId || order?.id,
    orderId: order?.order_id || order?.orderId || order?.id,
    customerName: order?.customer_name || order?.customerName || 'Customer',
    customerMobile: order?.customer_mobile || order?.customerMobile || '',
    address: order?.address || order?.fullAddress || order?.full_address || '',
    area: order?.area || order?.areaKey || '',
    amount: Number(order?.amount || order?.displayTotal || order?.final_total || 0),
    paymentStatus: String(order?.payment_status || order?.paymentStatus || 'unpaid').toLowerCase(),
    tokenNumber: order?.token_number || order?.tokenNumber || '',
    status: String(order?.status || order?.delivery_status || 'assigned').toLowerCase(),
    latitude: order?.latitude || order?.customer_latitude || null,
    longitude: order?.longitude || order?.customer_longitude || null,
  });

  const normalizeTrip = (trip) => {
    if (!trip) return null;

    return {
      ...trip,
      id: trip?.id || trip?.trip_id,
      tripNo: trip?.trip_no || trip?.tripNo || `TRP-${trip?.id || ''}`,
      riderId: trip?.rider_id || trip?.riderId,
      riderName: trip?.rider_name || trip?.riderName || rider?.name || '',
      riderMobile: trip?.rider_mobile || trip?.riderMobile || rider?.mobile || '',
      status: String(trip?.status || trip?.trip_status || 'assigned').toLowerCase(),
      orders: Array.isArray(trip?.orders) ? trip.orders.map(normalizeOrder) : [],
      totalOrders: Number(trip?.total_orders || trip?.totalOrders || trip?.orders?.length || 0),
      totalAmount: Number(trip?.total_amount || trip?.totalAmount || 0),
      createdAt: trip?.created_at || trip?.createdAt,
      assignedAt: trip?.assigned_at || trip?.assignedAt,
    };
  };

  const selectedOrder = useMemo(() => {
    const orders = activeTrip?.orders || [];
    return (
      orders.find((order) => String(order.id) === String(selectedOrderId)) ||
      orders.find((order) => !['delivered', 'cancelled', 'failed'].includes(order.status)) ||
      orders[0] ||
      null
    );
  }, [activeTrip, selectedOrderId]);

  const tripProgress = useMemo(() => {
    const orders = activeTrip?.orders || [];
    const delivered = orders.filter((order) => order.status === 'delivered').length;

    return {
      total: orders.length,
      delivered,
      pending: Math.max(0, orders.length - delivered),
      percent: orders.length ? Math.round((delivered / orders.length) * 100) : 0,
    };
  }, [activeTrip]);

  const login = async (event) => {
    event.preventDefault();

    const mobile = loginForm.mobile.replace(/\D/g, '').slice(0, 10);
    const pin = loginForm.pin.trim();

    if (!vendorId) {
      showMessage('Invalid delivery link. Vendor id missing hai.', 'error');
      return;
    }

    if (mobile.length !== 10 || !pin) {
      showMessage('10 digit mobile number aur PIN required hai.', 'error');
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${DELIVERY_API}/rider-login`, {
        vendorId,
        vendor_id: vendorId,
        vendorType: String(vendorType || 'fastfood').toLowerCase(),
        mobile,
        pin,
      });

      const nextRider = {
        ...(res.data?.rider || {}),
        id: res.data?.rider?.id || res.data?.rider?.rider_id,
        name: res.data?.rider?.name || res.data?.rider?.rider_name || 'Delivery Boy',
        mobile: res.data?.rider?.mobile || res.data?.rider?.rider_mobile || mobile,
        vendorId: res.data?.vendorId || vendorId,
        vendorType: res.data?.vendorType || vendorType,
      };

      setRider(nextRider);
      setVendorId(String(nextRider.vendorId || vendorId));
      setVendorType(String(nextRider.vendorType || vendorType || 'fastfood').toLowerCase());
      localStorage.setItem('delivery_boy_rider', JSON.stringify(nextRider));
      showMessage('Login successful.');
      fetchActiveTrip(nextRider);
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Login failed.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    stopLocationTracking();
    localStorage.removeItem('delivery_boy_rider');
    setRider(null);
    setActiveTrip(null);
    setSelectedOrderId(null);
    setTokenByOrder({});
    showMessage('Logged out.');
  };

  const fetchActiveTrip = async (riderOverride = null) => {
    const currentRider = riderOverride || rider;

    if (!currentRider?.id) return;

    try {
      setTripLoading(true);

      const res = await axios.get(`${DELIVERY_API}/rider/${currentRider.id}/active-trip`, {
        params: {
          vendorId: currentRider.vendorId || vendorId,
          vendorType: currentRider.vendorType || vendorType,
        },
      });

      const trip = normalizeTrip(res.data?.trip || null);
      setActiveTrip(trip);

      if (trip?.orders?.length) {
        const firstPending =
          trip.orders.find((order) => !['delivered', 'cancelled', 'failed'].includes(order.status)) ||
          trip.orders[0];

        setSelectedOrderId(firstPending.id);
      }
    } catch (err) {
      setActiveTrip(null);

      if (err?.response?.status === 404) {
        showMessage('Abhi koi active trip assigned nahi hai.', 'error');
      } else {
        showMessage(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Active trip load failed.',
          'error'
        );
      }
    } finally {
      setTripLoading(false);
    }
  };

  const updateTripStatus = async (status) => {
    if (!activeTrip?.id || actionLoading) return;

    const activeVendorId = rider?.vendorId || vendorId;
    const activeVendorType = String(
      rider?.vendorType || vendorType || 'fastfood'
    ).toLowerCase();

    try {
      setActionLoading(true);

      const res = await axios.patch(`${DELIVERY_API}/trips/${activeTrip.id}/status`, {
        status,
        riderId: rider?.id,
        vendorId: activeVendorId,
        vendorType: activeVendorType,
        latitude: lastLocation?.latitude || null,
        longitude: lastLocation?.longitude || null,
      });

      setActiveTrip(normalizeTrip(res.data?.trip || { ...activeTrip, status }));
      showMessage(`${TRIP_STATUS_LABELS[status] || status} successful.`);
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Trip status update failed.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const confirmToken = async (order) => {
    if (!order?.tripOrderId || actionLoading) return;

    const tokenNumber = String(tokenByOrder[order.tripOrderId] || '').trim();

    if (!tokenNumber) {
      showMessage('Customer ka token number enter karein.', 'error');
      return;
    }

    const activeVendorId = rider?.vendorId || vendorId;
    const activeVendorType = String(
      rider?.vendorType || vendorType || 'fastfood'
    ).toLowerCase();

    try {
      setActionLoading(true);

      const res = await axios.post(`${DELIVERY_API}/trip-orders/${order.tripOrderId}/confirm-token`, {
        vendorId: activeVendorId,
        vendorType: activeVendorType,
        riderId: rider?.id,
        tokenNumber,
        latitude: lastLocation?.latitude || null,
        longitude: lastLocation?.longitude || null,
      });

      const trip = normalizeTrip(res.data?.trip || null);
      setActiveTrip(trip);

      setTokenByOrder((prev) => ({
        ...prev,
        [order.tripOrderId]: '',
      }));

      showMessage('Delivery confirmed successfully.');

      const nextPending = trip?.orders?.find(
        (item) => !['delivered', 'cancelled', 'failed'].includes(item.status)
      );

      if (nextPending) {
        setSelectedOrderId(nextPending.id);
      }
    } catch (err) {
      showMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Invalid token number.',
        'error'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const sendLocationToBackend = async (coords, { force = false } = {}) => {
    const currentRider = riderRef.current || rider;
    const currentTrip = activeTripRef.current || activeTrip;

    if (!currentRider?.id) return;

    const now = Date.now();
    if (!force && now - lastLocationSentAtRef.current < 10000) return;

    lastLocationSentAtRef.current = now;

    try {
      await axios.post(`${DELIVERY_API}/rider-location`, {
        vendorId: currentRider.vendorId || vendorId,
        vendorType: currentRider.vendorType || vendorType,
        riderId: currentRider.id,
        tripId: currentTrip?.id || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy || null,
        updatedAt: coords.updatedAt,
      });
    } catch {
      // silent because location update should not disturb delivery boy
    }
  };

  const startLocationTracking = () => {
    if (!('geolocation' in navigator)) {
      showMessage('Is phone me location support nahi hai.', 'error');
      return;
    }

    if (watchIdRef.current) {
      showMessage('Live location already on hai.');
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          updatedAt: new Date().toISOString(),
        };

        setLastLocation(coords);
        setLocationAllowed(true);
        sendLocationToBackend(coords, { force: !lastLocation });
      },
      (error) => {
        setLocationAllowed(false);
        showMessage(error?.message || 'Location permission denied.', 'error');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 15000,
      }
    );

    showMessage('Live location started.');
  };

  const stopLocationTracking = () => {
    if (watchIdRef.current && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setLocationAllowed(false);
  };

  useEffect(() => {
    if (rider?.id) {
      fetchActiveTrip(rider);
    }

    return () => {
      stopLocationTracking();
    };
  }, []);

  const getNavigateUrl = (order) => {
    if (order?.latitude && order?.longitude) {
      return `https://www.google.com/maps/dir/?api=1&destination=${order.latitude},${order.longitude}`;
    }

    const query = encodeURIComponent(order?.address || order?.area || '');
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const getMapEmbedUrl = (order) => {
    if (order?.latitude && order?.longitude) {
      return `https://maps.google.com/maps?q=${order.latitude},${order.longitude}&z=16&output=embed`;
    }

    const query = encodeURIComponent(order?.address || order?.area || '');
    return `https://maps.google.com/maps?q=${query}&z=16&output=embed`;
  };

  const openMapInsidePage = (order) => {
    if (!order?.address && !order?.area && !(order?.latitude && order?.longitude)) {
      showMessage('Customer location/address missing hai.', 'error');
      return;
    }

    setMapOrder(order);
    showMessage('Map opened in same page.');
  };

  const openTurnByTurnNavigation = (order) => {
    window.location.href = getNavigateUrl(order);
  };

  const callCustomer = (mobile) => {
    if (!mobile) {
      showMessage('Customer mobile number missing hai.', 'error');
      return;
    }

    window.location.href = `tel:${mobile}`;
  };

  const renderToast = () =>
    message ? (
      <div className={`dba-toast ${messageType}`}>
        <span>{messageType === 'error' ? '⚠️' : '✅'}</span>
        <p>{message}</p>
        <button type="button" onClick={() => setMessage('')}>×</button>
      </div>
    ) : null;

  const renderLogin = () => (
    <main className="delivery-boy-page login">
      {renderToast()}

      <section className="dba-login-card">
        <div className="dba-brand">
          <span>🛵</span>
          <div>
            <h1>Delivery Boy Login</h1>
            <p>Vendor delivery link se login karein</p>
          </div>
        </div>

        <div className="dba-vendor-box">
          <small>Vendor Link</small>
          <strong>{vendorId ? `Vendor #${vendorId}` : 'Vendor missing'}</strong>
        </div>

        <form onSubmit={login} className="dba-login-form">
          <label>
            Mobile Number
            <input
              type="tel"
              value={loginForm.mobile}
              onChange={(e) =>
                setLoginForm((prev) => ({
                  ...prev,
                  mobile: e.target.value.replace(/\D/g, '').slice(0, 10),
                }))
              }
              placeholder="10 digit mobile"
              inputMode="numeric"
            />
          </label>

          <label>
            PIN
            <input
              type="password"
              value={loginForm.pin}
              onChange={(e) =>
                setLoginForm((prev) => ({
                  ...prev,
                  pin: e.target.value.replace(/\D/g, '').slice(0, 6),
                }))
              }
              placeholder="4-6 digit PIN"
              inputMode="numeric"
            />
          </label>

          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="dba-help-note">
          Vendor se mila hua mobile number aur PIN use karein. Vendor ka password yahan required nahi hai.
        </div>
      </section>
    </main>
  );

  const renderHeader = () => (
    <header className="dba-header">
      <div>
        <span>Delivery Boy</span>
        <h1>{rider?.name || 'Rider'}</h1>
        <p>{rider?.mobile || ''}</p>
      </div>

      <button type="button" onClick={logout}>
        Logout
      </button>
    </header>
  );

  const renderTripSummary = () => (
    <section className="dba-trip-summary">
      <div className="dba-trip-top">
        <div>
          <small>Trip</small>
          <strong>{activeTrip?.tripNo || 'No Trip'}</strong>
          <span>{activeTrip ? dateTime(activeTrip.assignedAt || activeTrip.createdAt) : '-'}</span>
        </div>

        <em className={activeTrip?.status || 'assigned'}>
          {TRIP_STATUS_LABELS[activeTrip?.status] || activeTrip?.status || 'No Trip'}
        </em>
      </div>

      <div className="dba-progress-box">
        <div className="dba-progress-text">
          <span>{tripProgress.delivered}/{tripProgress.total} Delivered</span>
          <b>{tripProgress.percent}%</b>
        </div>
        <div className="dba-progress-bar">
          <span style={{ width: `${tripProgress.percent}%` }}></span>
        </div>
      </div>

      <div className="dba-trip-actions">
        <button
          type="button"
          disabled={!activeTrip || actionLoading || activeTrip.status !== 'assigned'}
          onClick={() => updateTripStatus('picked_up')}
        >
          Picked Up
        </button>

        <button
          type="button"
          disabled={!activeTrip || actionLoading || !['assigned', 'picked_up'].includes(activeTrip.status)}
          onClick={() => updateTripStatus('out_for_delivery')}
        >
          Out for Delivery
        </button>
      </div>
    </section>
  );

  const renderLocationCard = () => (
    <section className="dba-location-card">
      <div>
        <strong>{locationAllowed ? 'Live location ON' : 'Live location OFF'}</strong>
        <span>
          {lastLocation
            ? `Updated: ${dateTime(lastLocation.updatedAt)}`
            : 'Location allow karne ke baad vendor tracking me location dikhegi.'}
        </span>
      </div>

      {locationAllowed ? (
        <button type="button" className="stop" onClick={stopLocationTracking}>
          Stop
        </button>
      ) : (
        <button type="button" onClick={startLocationTracking}>
          Allow Location
        </button>
      )}
    </section>
  );

  const renderOrderList = () => (
    <section className="dba-order-list">
      <div className="dba-section-head">
        <h2>Assigned Orders</h2>
        <button type="button" onClick={() => fetchActiveTrip()}>
          Refresh
        </button>
      </div>

      {activeTrip?.orders?.length ? (
        activeTrip.orders.map((order, index) => (
          <button
            type="button"
            className={`dba-order-pill ${
              String(selectedOrder?.id) === String(order.id) ? 'active' : ''
            } ${order.status}`}
            key={order.id || index}
            onClick={() => setSelectedOrderId(order.id)}
          >
            <span>{index + 1}</span>
            <div>
              <strong>#{order.orderId}</strong>
              <small>{order.customerName}</small>
            </div>
            <em>{ORDER_STATUS_LABELS[order.status] || order.status}</em>
          </button>
        ))
      ) : (
        <div className="dba-empty">
          <strong>No assigned orders</strong>
          <p>Vendor jab trip assign karega tab orders yahan dikhenge.</p>
        </div>
      )}
    </section>
  );

  const renderSelectedOrder = () => {
    if (!selectedOrder) {
      return (
        <section className="dba-selected-order">
          <div className="dba-empty">
            <strong>No order selected</strong>
            <p>Assigned order select karein.</p>
          </div>
        </section>
      );
    }

    const delivered = selectedOrder.status === 'delivered';

    return (
      <section className="dba-selected-order">
        <div className="dba-order-card-head">
          <div>
            <small>Order #{selectedOrder.orderId}</small>
            <h2>{selectedOrder.customerName}</h2>
            <p>{selectedOrder.customerMobile || 'Mobile missing'}</p>
          </div>

          <em className={selectedOrder.status}>
            {ORDER_STATUS_LABELS[selectedOrder.status] || selectedOrder.status}
          </em>
        </div>

        <div className="dba-address-box">
          <strong>Delivery Address</strong>
          <p>{selectedOrder.address || selectedOrder.area || 'Address missing'}</p>
        </div>

        <div className="dba-order-meta-grid">
          <div>
            <small>Amount</small>
            <strong>{money(selectedOrder.amount)}</strong>
          </div>
          <div>
            <small>Payment</small>
            <strong>{selectedOrder.paymentStatus}</strong>
          </div>
          <div>
            <small>Token</small>
            <strong>{selectedOrder.tokenNumber ? 'Required' : 'Missing'}</strong>
          </div>
        </div>

        <div className="dba-customer-actions">
          <button type="button" onClick={() => openMapInsidePage(selectedOrder)}>
            Open Map
          </button>

          <button type="button" className="navigate" onClick={() => openTurnByTurnNavigation(selectedOrder)}>
            Start Navigation
          </button>

          <button type="button" onClick={() => callCustomer(selectedOrder.customerMobile)}>
            Call
          </button>
        </div>

        <div className="dba-token-box">
          <label>
            Enter Token Number
            <input
              value={tokenByOrder[selectedOrder.tripOrderId] || ''}
              onChange={(e) =>
                setTokenByOrder((prev) => ({
                  ...prev,
                  [selectedOrder.tripOrderId]: e.target.value.trim(),
                }))
              }
              placeholder="Customer token number"
              disabled={delivered}
            />
          </label>

          <button
            type="button"
            disabled={actionLoading || delivered}
            onClick={() => confirmToken(selectedOrder)}
          >
            {delivered ? 'Delivered' : 'Confirm Delivered'}
          </button>
        </div>
      </section>
    );
  };

  const renderMapSheet = () =>
    mapOrder ? (
      <section className="dba-map-sheet">
        <div className="dba-map-sheet-head">
          <div>
            <small>Map View</small>
            <strong>{mapOrder.customerName}</strong>
            <span>{mapOrder.address || mapOrder.area || 'Customer location'}</span>
          </div>

          <button type="button" onClick={() => setMapOrder(null)}>
            ×
          </button>
        </div>

        <div className="dba-map-frame-wrap">
          <iframe
            title="Customer Location Map"
            src={getMapEmbedUrl(mapOrder)}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <div className="dba-map-actions">
          <button type="button" onClick={() => openTurnByTurnNavigation(mapOrder)}>
            Start Turn-by-Turn
          </button>
          <button type="button" className="close" onClick={() => setMapOrder(null)}>
            Close Map
          </button>
        </div>
      </section>
    ) : null;

  if (!rider) return renderLogin();

  return (
    <main className="delivery-boy-page">
      {renderToast()}
      {renderHeader()}
      {renderMapSheet()}

      {tripLoading ? (
        <section className="dba-loading">
          <div className="dba-loader"></div>
          <strong>Loading assigned trip...</strong>
        </section>
      ) : (
        <>
          {renderTripSummary()}
          {renderLocationCard()}
          {renderOrderList()}
          {renderSelectedOrder()}
        </>
      )}
    </main>
  );
};

export default DeliveryBoyApp;
